"use client";
import ImageWithSkeleton from "@/components/ui/ImageWithSkeleton";
import { useAppContext } from "@/providers/AppProvider";
import { ICaseStudy, IFile, IMetric } from "@/types";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Clock, Moon, Sun, Tag } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

interface CaseStudiesClientPageProps {
    initialCaseStudies?: ICaseStudy[];
}

export default function CaseStudiesClientPage({
    initialCaseStudies = [],
}: CaseStudiesClientPageProps) {
    const { theme, toggleTheme, caseStudies, setCaseStudies } = useAppContext();
    const [selectedTag, setSelectedTag] = useState<string>("All");

    // Initialize from props immediately if available, otherwise check context
    const effectiveCaseStudies =
        caseStudies.length > 0
            ? caseStudies
            : initialCaseStudies.length > 0
              ? initialCaseStudies
              : [];
    const [isLoading, setIsLoading] = useState(
        effectiveCaseStudies.length === 0,
    );

    useEffect(() => {
        if (initialCaseStudies.length > 0 && caseStudies.length === 0) {
            setCaseStudies(initialCaseStudies);
        } else if (
            caseStudies.length === 0 &&
            initialCaseStudies.length === 0
        ) {
            const fetchCaseStudies = async () => {
                try {
                    const res = await fetch("/api/case-studies");
                    const data = await res.json();
                    if (data.success) {
                        setCaseStudies(data.data);
                    }
                } catch (err) {
                    console.error("Failed to load case studies", err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchCaseStudies();
        }
    }, [initialCaseStudies, caseStudies, setCaseStudies]);

    // Extract unique tags from all case studies
    const allTags = Array.from(
        new Set(effectiveCaseStudies.flatMap((cs) => cs.tags || [])),
    ).filter(Boolean);

    const filteredCaseStudies = effectiveCaseStudies.filter((cs) => {
        if (selectedTag === "All") return true;
        return cs.tags?.includes(selectedTag);
    });

    return (
        <div className="min-h-screen bg-background-color pb-16 text-text-color">
            {/* Top Navigation Bar */}
            <header className="sticky top-0 z-50 border-b border-border-color bg-card-bg px-6 py-3.5">
                <div className="mx-auto flex max-w-6xl items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link
                            href="/"
                            className="flex items-center gap-1.5 rounded-xl border border-border-color bg-card-bg px-3 py-1.5 text-xs font-semibold text-text-color transition hover:border-primary hover:text-primary"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Main Portfolio
                        </Link>
                        <div className="h-4 w-px bg-border-color" />
                        <h1 className="text-sm font-bold text-primary sm:text-base">
                            Sahil Atahar{" "}
                            <span className="text-muted-foreground hidden text-xs font-normal sm:inline">
                                — Engineering Case Studies
                            </span>
                        </h1>
                    </div>
                    <button
                        onClick={toggleTheme}
                        className="flex items-center justify-center rounded-xl border border-border-color bg-card-bg p-2 text-text-color outline-none transition hover:border-primary hover:text-primary"
                        aria-label="Toggle Theme"
                    >
                        {theme === "light" ? (
                            <Moon className="h-4 w-4" />
                        ) : (
                            <Sun className="h-4 w-4" />
                        )}
                    </button>
                </div>
            </header>
            {/* Main Content Area */}
            <motion.main
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mx-auto max-w-6xl space-y-8 px-4 py-8"
            >
                {/* Hero Banner */}
                <div className="space-y-2 text-center sm:text-left">
                    <h2 className="text-3xl font-black text-primary sm:text-4xl">
                        Case Studies & System Architecture
                    </h2>
                    <p className="text-text-color/80 max-w-2xl text-sm leading-relaxed">
                        In-depth articles covering real-world engineering
                        challenges, performance optimizations, scalable system
                        designs, and production implementations by Sahil Atahar.
                    </p>
                </div>
                {/* Tag Filter Bar */}
                {!isLoading && caseStudies.length > 0 && allTags.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-border-color pt-2">
                        <span className="text-muted-foreground mr-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                            <Tag className="h-3.5 w-3.5 text-primary" /> Filter:
                        </span>
                        <button
                            onClick={() => setSelectedTag("All")}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                                selectedTag === "All"
                                    ? "border border-primary bg-primary/15 text-primary shadow-md"
                                    : "border border-border-color bg-card-bg text-text-color hover:border-primary"
                            }`}
                        >
                            All ({caseStudies.length})
                        </button>
                        {allTags.map((tag) => {
                            const count = caseStudies.filter((cs) =>
                                cs.tags?.includes(tag),
                            ).length;
                            return (
                                <button
                                    key={tag}
                                    onClick={() => setSelectedTag(tag)}
                                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                                        selectedTag === tag
                                            ? "border border-primary bg-primary/15 text-primary shadow-md"
                                            : "border border-border-color bg-card-bg text-text-color hover:border-primary"
                                    }`}
                                >
                                    #{tag} ({count})
                                </button>
                            );
                        })}
                    </div>
                )}

                {isLoading ? (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, idx) => (
                            <CaseStudySkeletonCard key={idx} />
                        ))}
                    </div>
                ) : filteredCaseStudies.length === 0 ? (
                    <div className="flex min-h-[250px] flex-col items-center justify-center rounded-3xl border border-dashed border-border-color p-12 text-center">
                        <BookOpen className="text-muted-foreground mb-3 h-12 w-12" />
                        <p className="text-base font-semibold text-text-color">
                            No case studies found for #{selectedTag}.
                        </p>
                        <button
                            onClick={() => setSelectedTag("All")}
                            className="mt-3 text-xs font-semibold text-primary underline"
                        >
                            Clear Filter
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredCaseStudies.map((cs, idx) => (
                            <PublicCaseStudyCard
                                key={cs._id}
                                caseStudy={cs}
                                index={idx}
                            />
                        ))}
                    </div>
                )}
            </motion.main>
        </div>
    );
}

function CaseStudySkeletonCard() {
    return (
        <div className="bg-card-bg/60 flex animate-pulse flex-col justify-between overflow-hidden rounded-2xl border border-border-color p-0 shadow-sm">
            <div>
                <div className="bg-border-color/30 aspect-video w-full" />
                <div className="space-y-3 p-5">
                    <div className="flex items-center gap-3">
                        <div className="bg-border-color/40 h-3.5 w-20 rounded-full" />
                        <div className="bg-border-color/40 h-3.5 w-16 rounded-full" />
                    </div>
                    <div className="bg-border-color/50 h-5 w-4/5 rounded-lg" />
                    <div className="space-y-1.5 pt-1">
                        <div className="bg-border-color/30 h-3.5 w-full rounded" />
                        <div className="bg-border-color/30 h-3.5 w-5/6 rounded" />
                        <div className="bg-border-color/30 h-3.5 w-2/3 rounded" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function PublicCaseStudyCard({
    caseStudy,
    index,
}: {
    caseStudy: ICaseStudy;
    index: number;
}) {
    const coverUrl = (caseStudy.coverImage as IFile)?.cloudinaryUrl || "";

    // Estimate reading time
    const words = (caseStudy.content || "").split(/\s+/).length;
    const readTime = Math.max(1, Math.ceil(words / 200));

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08, duration: 0.4 }}
        >
            <Link
                href={`/case-studies/${caseStudy.slug}`}
                prefetch={true}
                className="group flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-border-color bg-card-bg transition duration-300 hover:border-primary hover:shadow-xl"
            >
                <div>
                    <div className="bg-card-bg/60 relative aspect-video w-full overflow-hidden">
                        <ImageWithSkeleton
                            src={coverUrl}
                            alt={caseStudy.title}
                            isLoadingUrl={false}
                            fill
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            containerClassName="relative h-full w-full"
                            className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                    </div>
                    <div className="space-y-3 p-5">
                        <div className="text-muted-foreground flex items-center gap-3 text-xs">
                            <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5 text-primary" />{" "}
                                {readTime} min read
                            </span>
                            {caseStudy.tags && caseStudy.tags.length > 0 && (
                                <span className="flex items-center gap-1 font-medium text-primary">
                                    <Tag className="h-3.5 w-3.5" /> #
                                    {caseStudy.tags[0]}
                                </span>
                            )}
                        </div>
                        <h3 className="line-clamp-2 text-lg font-bold text-primary transition group-hover:text-primary/90">
                            {caseStudy.title}
                        </h3>
                        <p className="text-text-color/90 line-clamp-3 text-xs leading-relaxed">
                            {caseStudy.summary}
                        </p>
                        {/* Key Metric Highlights */}
                        {caseStudy.metrics && caseStudy.metrics.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {caseStudy.metrics.map(
                                    (m: IMetric, idx: number) => (
                                        <div
                                            key={idx}
                                            className="rounded-lg border border-primary bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary"
                                        >
                                            {m.label}:{" "}
                                            <span className="font-bold">
                                                {m.value}
                                            </span>
                                        </div>
                                    ),
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
